import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMars, faVenus } from "@fortawesome/free-solid-svg-icons";
import { getFirestore, collection, onSnapshot } from "firebase/firestore";
import { useEventId } from "../EventContext";
import { getFlagCode } from "@vionixasia/shared-components/lib/countryCodes";
import { displayElapsed } from "../utils/finishTime";
import { completedLoopsOf, latestLoopOf, loopClockOf, rankLoopResults, rankRelayResults } from "../utils/loopDisplay";

/**
 * ANNOUNCER — finish-line feed for the MC.
 *
 * Ordered by ARRIVAL, newest first — the opposite of the results pages, which
 * rank by time. The MC reads the top card aloud, so the newest finisher has to
 * be the one under their eyes, and it has to get there without a click.
 *
 * Visual language is deliberately the same as Results.jsx: slate borders,
 * rounded-card panels, outlined pills, bold uppercase names. Only the type
 * scale is larger, because this is read at arm's length with a microphone.
 */

// How long a card keeps its "just arrived" highlight.
const FRESH_MS = 45000;

// Rows added per scroll step, matching the results page's page size.
const ANNOUNCER_PAGE = 100;

const API = import.meta.env.VITE_API_BASE_URL;

// Layout preview for `?demo=1` only — never shown during a live race. Nothing
// is written anywhere; these rows exist purely to render the page with content.
const DEMO_ROWS = [
  { bib: "6001", name: "Stefani Dyasih Ruswanti" },
  { bib: "6002", name: "Karmila Dewi Filayati" },
  { bib: "6003", name: "Salmah" },
  { bib: "6004", name: "Ika Nugrahanti" },
  { bib: "6005", name: "Nova Ruliza" },
  { bib: "6006", name: "Eliza Saliman" },
  { bib: "6007", name: "Yuriko Sonambela" },
  { bib: "6008", name: "Joanna Halim" },
  { bib: "6009", name: "Fourtina" },
  { bib: "6010", name: "Luisa Marina Aegiputri" },
].map((r, i) => {
  const secs = 2100 + i * 137;
  const two = (n) => String(n).padStart(2, "0");
  return {
    ...r,
    categoryId: "6K",
    race: "FUN TRAIL 6K",
    gender: "female",
    nationality: "Indonesia",
    finishTime: `${two(Math.floor(secs / 3600))}:${two(Math.floor((secs % 3600) / 60))}:${two(secs % 60)}`,
    overallRank: i + 1,
    genderRank: i + 1,
    finishMatTime: new Date(Date.now() - i * 137000).toISOString(),
  };
});

const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// The finish crossing itself. checkpoints.finish is what Results.jsx treats as
// authoritative; finishMatTime is the raw mat read. lastCheckpoint is NOT a
// finish — it is set by any checkpoint, so using it here put runners who had
// only passed CP1 into the MC's finisher feed.
const finishInstantOf = (r) => {
  const t = r.checkpoints?.finish?.time || r.finishMatTime || null;
  const ms = t ? new Date(t).getTime() : 0;
  if (Number.isFinite(ms) && ms) return ms;

  // Looping events have no `finish` key: the arrival the MC calls is the latest
  // lap crossing, stored as checkpoints.<cp>.loopN. Without this the announcer
  // reported 0 finishers for an entire lap-based event.
  let latest = 0;
  Object.values(r.checkpoints || {}).forEach((cp) => {
    if (!cp || typeof cp !== "object") return;
    Object.entries(cp).forEach(([k, v]) => {
      if (!/^loop\d+$/.test(k)) return;
      const n = v?.time ? new Date(v.time).getTime() : 0;
      if (Number.isFinite(n) && n > latest) latest = n;
    });
  });
  return latest;
};

// A buzzer-timed race records only an elapsed duration: the athlete's clock
// starts on their own press, so there is no mat crossing and no clock-of-day
// arrival at all. `updatedAt` (when the operator posted the time) is the only
// instant these rows carry, and it is what orders the MC's feed — newest post
// on top, exactly like a newest crossing.
const isDurationRow = (r) => !!(r.recordedDuration || r.finishDuration);

const postedInstantOf = (r) => {
  const ms = r.updatedAt ? new Date(r.updatedAt).getTime() : 0;
  return Number.isFinite(ms) ? ms : 0;
};

// Arrival instant used for ordering — the finish crossing.
const arrivalOf = (r) =>
  finishInstantOf(r) || (isDurationRow(r) ? postedInstantOf(r) : 0);

// Clock-of-day the runner crossed the line, "HH:mm:ss".
//
// The stored value is a full ISO timestamp, so rendering r.finishTime raw put
// the date and milliseconds on screen. Results.jsx formats before display; the
// announcer has to do the same. A value that is already "HH:mm:ss" (the demo
// rows, and any pre-formatted string) is passed through untouched.
const finishClockOf = (r, category = null) => {
  // Buzzer rows have no clock-of-day. The number the MC calls is the elapsed
  // duration itself, already stored formatted ("00:04:58.000"); trim the
  // milliseconds so it reads like every other time on the board.
  if (isDurationRow(r)) {
    const dur = String(r.recordedDuration || r.finishDuration).trim();
    return displayElapsed(dur, category) || "-";
  }
  // MYLAPS stores the elapsed time at millisecond precision — "00:06:16.984".
  // The old test demanded a bare HH:mm:ss, so every real finish failed it and
  // fell through to the crossing instant below: BIB 125 finished in 00:06:16
  // and the MC's board read 15:16:16, the wall clock. Accept the fractional
  // part and drop it, the way the buzzer rows already do.
  const raw = r.finishTime || r.gunTime || null;
  if (typeof raw === "string" && /^\d{1,2}:\d{2}:\d{2}(\.\d+)?$/.test(raw.trim())) {
    return raw.trim().replace(/\.\d+$/, "");
  }
  const ms = finishInstantOf(r) || (raw ? new Date(raw).getTime() : 0);
  if (!Number.isFinite(ms) || !ms) return "-";
  return new Date(ms).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const clockOf = (ms) =>
  ms
    ? new Date(ms).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
    : "--:--:--";

// Desktop row — same table shell, borders and hover lift as Results.jsx.
const DesktopRow = ({ r, fresh, isLooping, category = null }) => {
  const gender = (r.gender || "").toLowerCase();
  return (
    <tr className="hover:-translate-y-[2px] transition-transform shadow-sm group">
      <td className={`bg-white px-4 py-4 whitespace-nowrap text-sm font-bold text-slate-900 uppercase truncate cursor-default rounded-l-card border-y border-l ${fresh ? "border-teal-500" : "border-slate-200"} group-hover:bg-slate-50 transition-colors`}>
        {capitalizeWords(r.name) || "-"}
      </td>
      <td className={`bg-white px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-center border-y ${fresh ? "border-teal-500" : "border-slate-200"} group-hover:bg-slate-50 transition-colors`}>
        <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
          {r.bib ? `BIB ${r.bib}` : "-"}
        </span>
      </td>
      <td className={`bg-white px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-center border-y ${fresh ? "border-teal-500" : "border-slate-200"} group-hover:bg-slate-50 transition-colors`}>
        {r.race || r.categoryId || "-"}
      </td>
      <td className={`bg-white px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-center border-y ${fresh ? "border-teal-500" : "border-slate-200"} group-hover:bg-slate-50 transition-colors`}>
        <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
          {r.isTeam ? "Team" : <><FontAwesomeIcon icon={gender === "male" || gender === "m" ? faMars : faVenus} className="mr-1" />{gender === "male" || gender === "m" ? "Male" : "Female"}</>}
        </span>
      </td>
      <td className={`bg-white px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-center border-y ${fresh ? "border-teal-500" : "border-slate-200"} group-hover:bg-slate-50 transition-colors`}>
        {isLooping ? (
          <span className="font-bold text-slate-800 tabular-nums">{completedLoopsOf(r)}</span>
        ) : (
          <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
            <img src={`https://flagcdn.com/w20/${getFlagCode(r.nationality).code}.png`} onError={(e) => { e.target.src = "https://flagcdn.com/w20/un.png"; }} className="w-4 h-3 mr-1 border border-slate-200 rounded object-cover" alt={getFlagCode(r.nationality).display} />
            {getFlagCode(r.nationality).display || "-"}
          </span>
        )}
      </td>
      {/* Gender placing in this runner's category — what the MC calls out.
          Shown the same way the results page shows it, so the board the MC
          reads and the board the crowd reads never disagree. */}
      <td className={`bg-white px-4 py-4 whitespace-nowrap text-sm text-center tabular-nums border-y ${fresh ? "border-teal-500" : "border-slate-200"} group-hover:bg-slate-50 transition-colors`}>
        {r._genderRank > 0 ? (
          <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-slate-50 text-slate-700 border border-slate-200 rounded-full font-bold text-xs">
            {r._genderRank}
          </span>
        ) : (
          <span className="text-slate-300">-</span>
        )}
      </td>
      <td className={`bg-white px-4 py-4 whitespace-nowrap text-sm font-semibold text-slate-700 text-center tabular-nums rounded-r-card border-y border-r ${fresh ? "border-teal-500" : "border-slate-200"} group-hover:bg-slate-50 transition-colors`}>
        {isLooping ? loopClockOf(r) : finishClockOf(r, category)}
      </td>
    </tr>
  );
};

// Mobile card — mirrors the md:hidden block in Results.jsx: fixed-width finish
// column so long names truncate instead of pushing the time off-screen, with a
// second row of quiet supporting detail.
const MobileCard = ({ r, fresh, isLooping, category = null }) => {
  const gender = (r.gender || "").toLowerCase();
  return (
    <div className={`bg-white rounded-card border shadow-sm p-2 overflow-hidden ${fresh ? "border-teal-500" : "border-slate-200"}`}>
      <table className="min-w-full table-fixed">
        <tbody>
          <tr>
            <td className="py-2 pl-2 text-sm font-bold text-slate-900 uppercase truncate">
              {capitalizeWords(r.name) || "-"}
            </td>
            <td className="w-24 py-2 pr-2 text-sm font-medium text-slate-700 text-right tabular-nums">
              {isLooping ? loopClockOf(r) : finishClockOf(r, category)}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="flex items-center gap-3 pb-2 pl-2 pr-2 border-t border-slate-200/50 pt-2 text-xs text-slate-500">
        {!isLooping && <span className="border border-slate-200 rounded-full px-2 py-1 inline-flex items-center justify-center min-h-[26px] whitespace-nowrap">
                            <img
                              src={`https://flagcdn.com/w20/${getFlagCode(r.nationality).code}.png`}
                              onError={(e) => { e.target.src = "https://flagcdn.com/w20/un.png"; }}
                              className="w-4 h-3 mr-1 border border-slate-200 rounded object-cover"
                              alt={getFlagCode(r.nationality).display}
                            />
                            {getFlagCode(r.nationality).display || "-"}
                          </span>}
        <span>BIB {r.bib || "-"}</span>
        <span>{r.isTeam ? "Team" : <FontAwesomeIcon icon={gender === "male" || gender === "m" ? faMars : faVenus} />}</span>
        {/* Announcer merges every category into one feed, so unlike the results
            page — already filtered by a category tab — the MC needs it per row. */}
        <span className="truncate">{r.race || r.categoryId || "-"}</span>
        {isLooping && <span className="font-semibold text-slate-700">{completedLoopsOf(r)} loops</span>}
        {r._genderRank > 0 && (
          <span className="font-semibold text-slate-700">
            {gender === "female" ? "P" : "L"}
            {r._genderRank}
          </span>
        )}
        <span className="flex-1" />

      </div>
    </div>
  );
};

export const Announcer = () => {
  const { realEventId: eventId } = useEventId();
  const [rows, setRows] = useState([]);
  // Every result doc, finished or not — the source for the per-category tally.
  const [allRows, setAllRows] = useState([]);
  const [cats, setCats] = useState([]);
  // Entrants per category, so the tally can show how many were never read.
  const [entriesByCat, setEntriesByCat] = useState({});
  const [err, setErr] = useState("");
  const [lastSync, setLastSync] = useState(null);
  // Ticks once a minute so a category flips from "on course" to unfinished the
  // moment its cut-off passes, without waiting for the next arrival.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const seenRef = useRef(new Set());
  const [freshBibs, setFreshBibs] = useState(new Set());
  const [shownCount, setShownCount] = useState(ANNOUNCER_PAGE);
  const annSentinelRef = useRef(null);

  // Opt-in only (?demo=1). It must never appear on its own during a race — the
  // MC would read out names that are not on the course.
  const demo =
    new URLSearchParams(window.location.search).get("demo") === "1" && rows.length === 0;

  useEffect(() => {
    if (!eventId) return;
    let alive = true;
    fetch(`${API}/api/timing/event_timing`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        const ev = (d || []).find((e) => e.eventId === eventId);
        setCats(
          (ev?.categories || []).map((c) => ({
            id: c.id,
            name: c.name,
            cutOffTime: c.cutOffTime || null,
            isLooping: c.isLooping || false,
            isRelay: c.isRelay || false,
            showHundredths: c.showHundredths === true,
            eventStatus: ev?.status || "ongoing",
          }))
        );
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [eventId]);

  // Entry count per category. The tally below can only see runners the mats
  // actually read, so without this the board cannot say how many entrants have
  // no crossing at all — at Sembalun 34 of the 203 starters in 42K/21K were
  // missed at the start mat and were simply invisible here.
  //
  // Fetched once: the entry list does not change mid-race, and a failure just
  // leaves the extra figure hidden rather than breaking the board.
  useEffect(() => {
    if (!eventId) return;
    let alive = true;
    fetch(`${API}/api/timing/participants?eventId=${eventId}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        const counts = {};
        (d?.participants || []).forEach((p) => {
          const id = p.categoryId || "-";
          counts[id] = (counts[id] || 0) + 1;
        });
        setEntriesByCat(counts);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [eventId]);

  // Firestore realtime instead of polling. The MC has to hear the name as the
  // runner crosses, and a 5 s poll is both too slow for that and far more
  // expensive: polling re-reads the whole result set every cycle, while
  // onSnapshot reads once and then only the documents that actually changed.
  useEffect(() => {
    if (!eventId) return;
    const db = getFirestore();
    const resultsRef = collection(db, "event_timing", eventId, "results");

    const unsub = onSnapshot(
      resultsRef,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Only runners who actually crossed — no time means nothing to announce.
        const arrived = list
          .filter((r) => arrivalOf(r) > 0)
          .sort((a, b) => arrivalOf(b) - arrivalOf(a));

        // Placing, for the MC to call as the runner comes through. The results
        // API computes this at query time and never stores it on the document,
        // so it is derived from this same snapshot — one live source, and the
        // board cannot disagree with the finish-desk receipt.
        //
        // Ranked WITHIN a category: a 7K finisher is not placed against the 42K
        // field. Ordering uses the elapsed time at full precision, so two
        // runners sharing a printed second still separate correctly.
        const byCat = {};
        arrived.forEach((r) => {
          const k = r.categoryId || r.race || "";
          (byCat[k] = byCat[k] || []).push(r);
        });
        Object.values(byCat).forEach((catRows) => {
          const ordered = catRows
            .slice()
            .sort((a, b) =>
              String(a.finishTime || a.gunTime || "").localeCompare(
                String(b.finishTime || b.gunTime || "")
              )
            );
          const seenGender = { male: 0, female: 0 };
          ordered.forEach((r, i) => {
            r._overallRank = i + 1;
            const g = (r.gender || "").toLowerCase();
            if (g === "male" || g === "female") {
              seenGender[g] += 1;
              r._genderRank = seenGender[g];
            } else {
              r._genderRank = null;
            }
          });
        });

        // Highlight arrivals we have not seen before, but never on the first
        // snapshot — otherwise every existing finisher flashes as "new".
        const firstLoad = seenRef.current.size === 0;
        const nextFresh = new Set();
        arrived.forEach((r) => {
          const key = `${r.bib}:${arrivalOf(r)}`;
          if (!firstLoad && !seenRef.current.has(key)) nextFresh.add(String(r.bib));
          seenRef.current.add(key);
        });

        setRows(arrived);
        setAllRows(list);
        setErr("");
        setLastSync(Date.now());

        if (nextFresh.size) {
          setFreshBibs((prev) => new Set([...prev, ...nextFresh]));
          nextFresh.forEach((bib) =>
            setTimeout(
              () =>
                setFreshBibs((prev) => {
                  const n = new Set(prev);
                  n.delete(bib);
                  return n;
                }),
              FRESH_MS
            )
          );
        }
      },
      (e) => setErr(e.message)
    );

    return () => unsub();
  }, [eventId]);

  const hasLoopingEvent = cats.some((category) => category.isLooping);
  const timingRows = useMemo(() => {
    const loopCategoryIds = new Set(cats.filter((category) => category.isLooping).map((category) => category.id));
    const standardRows = allRows.filter((result) => !loopCategoryIds.has(result.categoryId));
    const loopRows = cats.filter((category) => category.isLooping).flatMap((category) => {
      const source = allRows.filter((result) => result.categoryId === category.id);
      return category.isRelay ? rankRelayResults(source) : rankLoopResults(source);
    });
    return [...standardRows, ...loopRows];
  }, [allRows, cats]);
  const allVisible = demo
    ? DEMO_ROWS
    : hasLoopingEvent
      ? timingRows.filter((result) => arrivalOf(result) > 0).sort((a, b) => arrivalOf(b) - arrivalOf(a))
      : rows;

  // Render in pages like Results. Every finisher is already in memory from the
  // snapshot, so this caps the DOM rather than the network: 670+ rows mounted at
  // once made the MC's feed stutter on each live update.
  const visible = useMemo(
    () => allVisible.slice(0, shownCount),
    [allVisible, shownCount]
  );

  // Shrink back when the feed is filtered/reset to fewer rows than are shown,
  // so the count in the footer can never exceed the list itself.
  useEffect(() => {
    setShownCount((prev) =>
      prev > allVisible.length ? Math.max(ANNOUNCER_PAGE, allVisible.length) : prev
    );
  }, [allVisible.length]);

  // A new arrival goes to the top of the list, so the window must stay anchored
  // there — never auto-grow just because rows were prepended.
  useEffect(() => {
    const sentinel = annSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        setShownCount((prev) =>
          prev >= allVisible.length ? prev : prev + ANNOUNCER_PAGE
        );
      },
      { rootMargin: "300px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [allVisible.length]);

  // Per-category finish rate. "Started" counts runners with a recorded start
  // crossing — the ones actually on course — rather than the entry list, so a
  // DNS never shows up as a runner still out there.
  const tally = React.useMemo(() => {
    const source = demo ? DEMO_ROWS : timingRows;
    const byCat = new Map();
    const bump = (id, key) => {
      if (!byCat.has(id)) byCat.set(id, { started: 0, finished: 0 });
      byCat.get(id)[key] += 1;
    };
    source.forEach((r) => {
      const id = r.categoryId || "-";
      // Any checkpoint at all proves the runner was on the route. Keying this on
      // the start mat alone dropped anyone whose chip was missed there — BIB 1347
      // has a CP1 but no start reading, and vanished from the tally entirely.
      // A loop checkpoint nests its reads (checkpoints.lap.loop1), so `cp.time`
      // alone missed every lap-based runner.
      const anyCp = Object.values(r.checkpoints || {}).some(
        (cp) => cp?.time || (cp && typeof cp === "object" && Object.keys(cp).some((k) => /^loop\d+$/.test(k)))
      );
      // A withdrawal the operator has already declared is not a runner still
      // out on the mountain. Without this, SMM 2026 BIB 2074 — marked DNF —
      // held the 21K at "1 on course" after the last finisher was in, and the
      // desk kept looking for a runner who had stopped.
      const withdrawn = ["DNF", "DNS", "DSQ"].includes(
        (r.status || "").toUpperCase()
      );
      const started =
        !withdrawn &&
        (!!(r.startMatTime || r.checkpoints?.start?.time) || anyCp);

      const cat = cats.find((c) => c.id === id);
      let finished;
      if (cat?.isLooping) {
        const laps = Object.values(r.checkpoints || {}).reduce((mx, cp) => {
          if (!cp || typeof cp !== "object") return mx;
          return Object.keys(cp).reduce((m, k) => {
            const n = /^loop(\d+)$/.test(k) ? parseInt(k.slice(4), 10) : 0;
            return n > m ? n : m;
          }, mx);
        }, 0);
        const declared = ["DNF", "DNS", "DSQ"].includes((r.status || "").toUpperCase());
        // On a loop race this counter means "has completed at least one loop".
        // A participant is not a finisher until the category closes.
        finished = !declared && laps > 0;
      } else if (cat?.isBuzzerTimed) {
        // No mats, so there is no "on course" to measure: a buzzer athlete
        // exists in this tally only once a time has been posted for them.
        const declared = ["DNF", "DNS", "DSQ"].includes((r.status || "").toUpperCase());
        finished = !declared && isDurationRow(r);
      } else {
        // Same rule as the loop and buzzer branches above: a declared
        // withdrawal holds no placing, so it is neither on course nor a
        // finisher even if a mat reading exists.
        finished = !withdrawn && finishInstantOf(r) > 0;
      }
      if (started || finished) bump(id, "started");
      if (finished) bump(id, "finished");
    });
    const order = cats.length ? cats : [...byCat.keys()].map((id) => ({ id, name: id }));
    return order
      .map((c) => {
        const t = byCat.get(c.id) || { started: 0, finished: 0 };
        const entries = entriesByCat[c.id] || 0;
        return {
          id: c.id,
          name: c.name || c.id,
          started: t.started,
          finished: t.finished,
          entries,
          // Entrants with no crossing at all. Before the gun that is simply the
          // whole field waiting; once running it is the chips the mats never
          // saw, and the operator needs to know the number rather than discover
          // it when a runner arrives at the desk with no time.
          unread: Math.max(0, entries - t.started),
          remaining: Math.max(0, t.started - t.finished),
          // Past the cut-off nobody is still running, so the remainder stops
          // being "on course" and becomes unfinished. Calling them on course
          // after the race has ended is simply wrong on screen.
          closed: (() => {
            const co = c.cutOffTime?.toDate ? c.cutOffTime.toDate() : c.cutOffTime;
            const ms = co ? new Date(co).getTime() : NaN;
            return Number.isFinite(ms) && nowMs > ms;
          })(),
          // Floor, not round: with 213 starters, 212 finished rounds up to 100%
          // and the MC reads "everyone is in" while a runner is still on course.
          // Only a genuinely complete field may show 100%.
          pct: t.started
            ? t.finished >= t.started
              ? 100
              : Math.min(99, Math.floor((t.finished / t.started) * 100))
            : 0,
        };
      });
  }, [demo, timingRows, cats, nowMs, entriesByCat]);

  const totals = tally.reduce(
    (a, c) => ({
      started: a.started + c.started,
      finished: a.finished + c.finished,
      remaining: a.remaining + c.remaining,
    }),
    { started: 0, finished: 0, remaining: 0 }
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-5">
          <div className="flex items-baseline gap-4">
            <h1 className="text-lg font-semibold text-slate-900">Announcer</h1>
            {totals.started > 0 && (
              <span className="text-xs text-slate-500 tabular-nums">
                {totals.finished}/{totals.started} {hasLoopingEvent ? "loop" : "finished"}
                <span className="text-slate-400">
                  {" "}· {totals.remaining}{" "}
                  {hasLoopingEvent
                    ? "awaiting Loop 1"
                    : tally.some((c) => !c.closed && c.remaining > 0) ? "on course" : "unfinished"}
                </span>
              </span>
            )}
          </div>
          <div className="text-right text-xs text-slate-500 tabular-nums">
            <div className="font-medium text-slate-700">{visible.length} {hasLoopingEvent ? "latest crossings" : "finishers"}</div>
            <div>
              {err ? (
                <span className="text-red-600">Error: {err}</span>
              ) : lastSync ? (
                `live · ${clockOf(lastSync)}`
              ) : (
                "loading\u2026"
              )}
            </div>
          </div>
        </div>

        {tally.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            {tally.map((c) => (
              <div key={c.id} className="bg-white rounded-card border border-slate-200 px-4 py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-900 truncate">{c.name}</span>
                  <span className="text-xs text-slate-500 tabular-nums shrink-0">{c.pct}%</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-teal-500 transition-[width] duration-500"
                    style={{ width: `${c.pct}%` }}
                  />
                </div>
                <div className="mt-2 flex items-baseline justify-between text-xs tabular-nums">
                  <span className="text-slate-600">
                    {c.finished}<span className="text-slate-400">/{c.started} {hasLoopingEvent ? "loop" : "finished"}</span>
                  </span>
                  <span className={c.remaining > 0 ? "font-medium text-slate-700" : "text-slate-400"}>
                    {c.remaining} {hasLoopingEvent ? "awaiting Loop 1" : c.closed ? "unfinished" : "on course"}
                    {/* Entrants the mats never read, carried on the same line
                        rather than a row of its own: it qualifies the count
                        beside it, and a second row made the card twice as tall
                        for one number. */}
                    {c.unread > 0 && (
                      <span className="text-slate-400"> · {c.unread} unread</span>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {visible.length === 0 && !err ? (
          <div className="bg-white rounded-card border border-slate-200 px-4 py-6 text-center text-sm text-slate-500">
            No race results available.
          </div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block overflow-x-auto pb-8">
              <table className="min-w-full border-separate" style={{ borderSpacing: "0 0.5rem" }}>
                <thead>
                  <tr>
                    <th className="bg-slate-50 px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider rounded-l-card border-y border-l border-slate-200">
                      Name
                    </th>
                    <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                      BIB
                    </th>
                    <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                      Category
                    </th>
                    <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                      Gender
                    </th>
                    <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                      {hasLoopingEvent ? "Loops" : "Nationality"}
                    </th>
                    <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                      Rank
                    </th>
                    <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider rounded-r-card border-y border-r border-slate-200">
                      {hasLoopingEvent ? "Last Crossing" : "Finish"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <DesktopRow key={`d-${r.bib}-${arrivalOf(r)}`} r={r} fresh={freshBibs.has(String(r.bib)) || freshBibs.has(String(r.lastCheckpoint?.runnerBib || ""))} isLooping={hasLoopingEvent} category={cats.find((c) => c.id === r.categoryId) || null} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="md:hidden space-y-3 pb-6">
              {visible.map((r) => (
                <MobileCard key={`m-${r.bib}-${arrivalOf(r)}`} r={r} fresh={freshBibs.has(String(r.bib)) || freshBibs.has(String(r.lastCheckpoint?.runnerBib || ""))} isLooping={hasLoopingEvent} category={cats.find((c) => c.id === r.categoryId) || null} />
              ))}
            </div>

            <div ref={annSentinelRef} className="pb-6 pt-2 min-h-[1px]">
              {visible.length < allVisible.length ? (
                <p className="text-center text-xs text-gray-400 py-4">
                  Showing {visible.length.toLocaleString()} of {allVisible.length.toLocaleString()}
                </p>
              ) : (
                <p className="text-center text-xs text-slate-400 py-2">
                  {allVisible.length.toLocaleString()} {hasLoopingEvent ? "athletes with completed loops" : `finisher${allVisible.length !== 1 ? "s" : ""}`}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Announcer;
